import type { TrainingFormationDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { formationMeta } from '../../lib/formations';
import { Badge, Button, Dialog, ErrorBlock, LoadingBlock, useToast } from '../ui';
import { FormationPreview, formationDiagram } from './FormationPitch';
import { SketchImageButton } from './ShareImageButton';

/**
 * Detailansicht einer Formation: grosse Ansicht auf dem Feld, Beschreibung und die Aktionen
 * "Kopieren und anpassen", bei eigenen Formationen "Bearbeiten" und "Archivieren".
 */
export function FormationDetailDialog({
  id,
  canEdit,
  onClose,
  onEdit,
  onCopied,
}: {
  id: string;
  canEdit: boolean;
  onClose: () => void;
  onEdit: (formation: TrainingFormationDto) => void;
  /** Die Kopie ist angelegt; die Seite wechselt zu ihr und oeffnet das Formular. */
  onCopied: (copy: TrainingFormationDto) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: formation, isLoading, error } = useQuery({
    queryKey: ['training-formation', id],
    queryFn: () => api.get<TrainingFormationDto>(`/api/training/formations/${id}`),
  });

  const fail = (fallback: string) => (err: unknown) => toast.show(err instanceof ApiError ? err.message : fallback, 'error');

  const copy = useMutation({
    mutationFn: () => api.post<TrainingFormationDto>(`/api/training/formations/${id}/copy`, {}),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['training-formations'] });
      toast.show('Kopie angelegt – du kannst sie jetzt anpassen');
      onCopied(created);
    },
    onError: fail('Die Formation konnte nicht kopiert werden'),
  });

  const archive = useMutation({
    mutationFn: () => api.del(`/api/training/formations/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['training-formations'] });
      await queryClient.invalidateQueries({ queryKey: ['training-formation'] });
      toast.show('Formation archiviert');
      onClose();
    },
    onError: fail('Die Formation konnte nicht archiviert werden'),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={formation ? `Formation ${formation.name}` : 'Formation'}
      wide
      footer={<Button variant="secondary" onClick={onClose}>Schließen</Button>}
    >
      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock error={error} />}
      {formation && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {formation.source === 'CATALOG' && <Badge tone="brand" title="Aus den Standardformationen – nicht änderbar, aber kopierbar">Standard</Badge>}
            <Badge tone="neutral">{formationMeta(formation)}</Badge>
            {formation.archived && <Badge tone="danger">Archiviert</Badge>}
          </div>
          {formation.description && <p className="text-sm text-slate-700">{formation.description}</p>}

          {canEdit && !formation.archived && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" loading={copy.isPending} onClick={() => copy.mutate()}>
                Kopieren und anpassen
              </Button>
              {formation.editable && (
                <>
                  <Button variant="secondary" size="sm" onClick={() => onEdit(formation)}>Bearbeiten</Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={archive.isPending}
                    onClick={() => {
                      if (window.confirm(`„${formation.name}“ archivieren?`)) archive.mutate();
                    }}
                  >
                    Archivieren
                  </Button>
                </>
              )}
            </div>
          )}

          <div className="mx-auto max-w-xs sm:max-w-sm">
            <div className="mb-1 flex justify-end">
              <SketchImageButton diagram={formationDiagram(formation.positions)} title={`Formation ${formation.name} (${formation.gameSize}er)`} />
            </div>
            <FormationPreview positions={formation.positions} title={`Formation ${formation.name}`} />
            <p className="mt-2 text-xs text-slate-500">Eigenes Tor unten, Spielrichtung nach oben. Der Torwart ist gelb.</p>
          </div>
        </div>
      )}
    </Dialog>
  );
}
